import Apis from "@/apis"
import { useSearchActionWithKey } from "@/features/useSearchActionWithKey"
import { IndexData } from "@/features_signal/indexData"
import { useRequest, useUpdateEffect } from "ahooks"
import { isString } from "lodash"
import { useEffect, useMemo, useState } from "react"
import { useParams } from "react-router-dom"
import { usePaginatedList } from "./usePaginatedRequest"

/**
 *
 * @param ids 解析参数
 * @returns
 */
function extractIds(ids: string[]) {
    return ids.reduce(
        (acc, id) => {
            const [venue, platform] = id.split("_")
            if (venue) acc.venueIds.push(venue)
            if (platform) acc.platformIds.push(platform)
            return acc
        },
        { venueIds: [] as string[], platformIds: [] as string[] },
    )
}

export function useSubPage() {
    const { categoryId = "" } = useParams<{
        categoryId: string
    }>()

    const { getActionList, setAction } = useSearchActionWithKey()
    const [sort = "", brands = ""] = getActionList(["sort", "brands"])
    const [keyword, setKeyword] = useState<string>("")

    const providerList = useMemo(() => {
        return IndexData.dataNav.value[categoryId] ?? []
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [IndexData.dataNav.value, categoryId])

    const { venueIds = [], platformIds = [] } = extractIds(
        isString(brands) ? brands.split(",") : [],
    )

    useEffect(() => {
        /**
         * 检查参数是否在该列表中, 不存在说明后台关闭了, 需要移除
         */
        if (providerList.length && brands?.length) {
            const existBrands: string[] = []
            const brandsArr = isString(brands) ? brands.split(",") : []
            brandsArr.forEach((brand) => {
                if (
                    providerList.some((provider) => brand === `${provider.venue_id}_${provider.id}`)
                ) {
                    existBrands.push(brand)
                }
            })
            setAction("brands", existBrands.join(","))
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [providerList, brands])

    const sorting = useMemo(() => {
        if (sort?.toLowerCase() === "popular") {
            return "sorting:desc"
        }
        if (sort?.toLowerCase() === "a-z") {
            return "en_name:asc"
        }
        return ""
    }, [sort])

    const { list, loading, noMore, reload, loadMore } = usePaginatedList<
        GameApiTypes.Fetcher.Props.GameListProps,
        GameApiTypes.Fetcher.Result.GameList
    >({
        initialQuery: {
            sorting,
            venue_id: venueIds.join(",") || "",
            platform_id: platformIds.join(",") || "",
            w: keyword || "",
            is_new: sort === "Newest" ? "1" : "0",
            is_hot: "0",
            game_type: getGameTypeBySection(categoryId),
        },
        fetcher: async props => {
            const response = await Apis.v1.refGetGameList(props)
            return response.data.data
        },
        reloadDeps: [categoryId, sort, sorting, venueIds.join(","), platformIds.join(",")],
    })

    const { run } = useRequest(async () => reload(), {
        debounceWait: 500,
        manual: true,
    })

    useUpdateEffect(() => {
        if (keyword.trim().length >= 3) {
            run()
        }
        if (keyword === "") {
            run()
        }
    }, [keyword])

    useEffect(() => {
        return () => setKeyword("")
    }, [categoryId])

    return {
        categoryId,
        sort,
        keyword,
        brands,
        ary: list,
        isLoading: loading,
        hasMore: !noMore,
        providerList,
        onLoadMore: loadMore,
        setAction,
        reload,
        setKeyword,
        run,
    }
}

/**
 * 1视讯 2捕鱼 3老虎机 5棋牌 7bingo
 * @param sec 游戏类型 slot | chess 等
 * @returns
 */

export function getGameTypeBySection(sec: string) {
    sec = sec.toLowerCase()
    if (["live"].includes(sec)) {
        return "1"
    }
    if (["fishing"].includes(sec)) {
        return "2"
    }
    if (["slot"].includes(sec)) {
        return "3"
    }
    if (["sport"].includes(sec)) {
        return "4"
    }
    if (["table game", "board games"].includes(sec)) {
        return "5"
    }
    if (["bingo"].includes(sec)) {
        return "7"
    }
    return "0"
}
